import type { GatewayHelloOk } from "../gateway.ts";
import type { SessionsListResult } from "../types.ts";
import { isSubagentSessionKey, parseAgentSessionKey } from "../../../../src/routing/session-key.js";
import { resolveMainSessionKey } from "./resolve-main-session.ts";
import { resolveAgentIdForSession } from "./resolve-session-agent.ts";

export type SessionCatalogKind = "main" | "fork" | "subagent" | "other";

export type SessionCatalogRow = {
  key: string;
  kind: SessionCatalogKind;
  agentId: string;
  updatedAt: number;
  spawnedBy?: string;
};

export function classifySessionCatalogKind(key: string): SessionCatalogKind {
  const trimmed = key.trim();
  if (!trimmed || isSubagentSessionKey(trimmed)) {
    return isSubagentSessionKey(trimmed) ? "subagent" : "other";
  }
  const parsed = parseAgentSessionKey(trimmed);
  if (!parsed) {
    return trimmed === "main" ? "main" : "other";
  }
  if (parsed.rest === "main" || parsed.rest.startsWith("main:")) {
    return "main";
  }
  if (parsed.rest.startsWith("fork:")) {
    return "fork";
  }
  return "other";
}

export function resolveAgentMainSessionKey(
  agentId: string,
  sessions: SessionsListResult | null,
  hello: GatewayHelloOk | null,
): string | null {
  const globalMain = resolveMainSessionKey(hello, sessions);
  const targetAgent = agentId.trim() || "main";
  for (const row of sessions?.sessions ?? []) {
    const key = row.key?.trim() ?? "";
    if (!key || isSubagentSessionKey(key)) {
      continue;
    }
    const parsed = parseAgentSessionKey(key);
    if (parsed?.agentId === targetAgent && parsed.rest === "main") {
      return key;
    }
  }
  if (targetAgent === "main" && globalMain) {
    return globalMain;
  }
  return `agent:${targetAgent}:main`;
}

export type SessionCatalogGroups = {
  agentId: string;
  mainSessionKey: string | null;
  main: SessionCatalogRow[];
  forks: SessionCatalogRow[];
  openTabs: SessionCatalogRow[];
  subagentsOfActive: SessionCatalogRow[];
  other: SessionCatalogRow[];
};

export function buildSessionCatalog(params: {
  activeSessionKey: string;
  openTabs: string[];
  sessions: SessionsListResult | null;
  hello: GatewayHelloOk | null;
}): SessionCatalogGroups {
  const activeKey = params.activeSessionKey.trim();
  const agentId = resolveAgentIdForSession(activeKey);
  const mainSessionKey = resolveAgentMainSessionKey(agentId, params.sessions, params.hello);
  const all = params.sessions?.sessions ?? [];
  const seen = new Set<string>();

  const rowFrom = (key: string): SessionCatalogRow | null => {
    const trimmed = key.trim();
    if (!trimmed || seen.has(trimmed)) {
      return null;
    }
    seen.add(trimmed);
    const entry = all.find((s) => s.key === trimmed);
    return {
      key: trimmed,
      kind: classifySessionCatalogKind(trimmed),
      agentId: resolveAgentIdForSession(trimmed),
      updatedAt: entry?.updatedAt ?? 0,
      spawnedBy: entry?.spawnedBy ?? undefined,
    };
  };

  const main: SessionCatalogRow[] = [];
  const forks: SessionCatalogRow[] = [];
  const openTabs: SessionCatalogRow[] = [];
  const subagentsOfActive: SessionCatalogRow[] = [];
  const other: SessionCatalogRow[] = [];

  if (mainSessionKey) {
    const row = rowFrom(mainSessionKey);
    if (row) {
      main.push(row);
    }
  }

  for (const key of params.openTabs) {
    const row = rowFrom(key);
    if (!row || row.agentId !== agentId) {
      continue;
    }
    openTabs.push(row);
  }

  const sorted = [...all].sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
  for (const entry of sorted) {
    const key = entry.key?.trim() ?? "";
    const row = rowFrom(key);
    if (!row || row.agentId !== agentId) {
      continue;
    }
    if (entry.spawnedBy === activeKey) {
      subagentsOfActive.push(row);
      continue;
    }
    if (row.kind === "fork") {
      forks.push(row);
    } else if (row.kind === "main" && !main.some((m) => m.key === row.key)) {
      main.push(row);
    } else if (row.kind === "other") {
      other.push(row);
    }
  }

  openTabs.sort((a, b) => {
    if (a.key === activeKey) {
      return -1;
    }
    if (b.key === activeKey) {
      return 1;
    }
    return b.updatedAt - a.updatedAt;
  });
  forks.sort((a, b) => b.updatedAt - a.updatedAt);

  return {
    agentId,
    mainSessionKey,
    main,
    forks,
    openTabs,
    subagentsOfActive,
    other,
  };
}
